import ModelMappingOrigin from "~/models/mapping/modelMappingOrigin";
import RelationIdentifier from "~/models/mapping/relationIdentifier";
import {IDefaultVOption, IVOptionSetLocal} from "~/interfaces/vSelectInterfaces";
import {ObjectModelRelationType} from "~/enums/objectModelRelationType";
import ObjectModelProperty from "~/models/objectModelProperties/objectModelProperty";
import ModelMappingOriginObjectModel, {IBuiltInPropertyValue} from "~/models/mapping/modelMappingOriginObjectModel";
import ModelMappingOriginFactory from "~/models/mapping/modelMappingOriginFactory";
import {ModelType} from "~/enums/modelType";
import ObjectModel from "~/models/objectModel";
import ModelMappingValueOrigin from "~/models/mapping/modelMappingValueOrigin";
import ObjectModelRelationDefinition from "~/models/objectModelRelations/objectModelRelationDefinition";
import ModelMappingOriginLookupField from "~/models/mapping/modelMappingOriginLookupField";
import {IOriginWrapper} from "~/interfaces/mappingInterfaces";
import MappingOriginOptionObjectModelRelation from "~/models/mapping/mappingOriginOptionObjectModelRelation";
import MappingOriginObjectModel from "~/models/mapping/mappingOriginObjectModel";
import OriginWrapperObjectModelRelation from "~/models/mapping/originWrapperObjectModelRelation";
import MappingOrigin from "~/models/mapping/mappingOrigin";
import { BuiltInTypesSetNumber } from '~/models/objectModelProperties/objectModelPropertyDefinition'

/**
 * Class used to handle relation identifiers in model mappings. Core functionality is handed off to
 *   ModelMappingOriginObjectModel to get/search values for level 3 model mapping values
 */
export default class ModelMappingOriginRelationIdentifier extends ModelMappingOrigin<RelationIdentifier> {
  canConsumeRecordId: boolean = true
  private _hasFetchedNestedObjectModelRelations: boolean = false

  get origin(): RelationIdentifier {
    return this.mappingOrigin.relationIdentifiers.find(r => r.client_id === this.id)
  }

  get identifier(): string {
    return this.origin?.client_id
  }

  get title(): string {
    return this.origin?.title
  }

  get modelTitle(): string {
    return this.origin?.modelTitle
  }

  get values(): Array<ObjectModelProperty | IBuiltInPropertyValue> {
    if (!Boolean(this.objectModelMappingOrigin)) return []
    return this.objectModelMappingOrigin.values
  }

  searchValues(query?: string, options: { filters?: ((value: string) => boolean)[], showUnmappedOnly?: boolean } = {}): Partial<this['values']> {
    if (!Boolean(this.objectModelMappingOrigin)) return []
    return this.objectModelMappingOrigin.searchValues(query, options)
  }

  valueOptions(source: ModelMappingValueOrigin = null): IDefaultVOption[] | Array<IVOptionSetLocal> {
    if (!this.hasModel) return []
    if (source?.isRecordId) {
      // only lookup fields can provided this information for now
      const sourceObjectModelId = (<ModelMappingOriginLookupField>this.modelMapping.sourceOrigin).objectModelId
      if (!sourceObjectModelId) return []

      return this.model.objectModelRelationsArray.filter(r => {
        return r.related_object_model_ids?.includes(sourceObjectModelId)
      }).map(r => {
        return ObjectModelRelationDefinition.mappingValueOption(r)
      })
    } else {
      return this.objectModelMappingOrigin.valueOptions(source)
    }
  }

  valueOptionsLoaded(source: ModelMappingValueOrigin = null): boolean {
    if (source?.isRecordId) {
      return this.model.objectModelRelationsArray.length > 0
    } else {
      return super.valueOptionsLoaded(source)
    }
  }

  async fetchValueOptions(source: ModelMappingValueOrigin) {
    if (source?.isRecordId) {
      return this.model.fetchObjectModelRelations()
    } else {
      return super.fetchValueOptions(source)
    }
  }

  get uploaderValueOptions(): IDefaultVOption[] {
    if (!Boolean(this.objectModelMappingOrigin)) return []
    return this.objectModelMappingOrigin.uploaderValueOptions;
  }

  get isSingle(): boolean {
    if (!this.origin?.relation) return null
    return this.origin.relation.relation_type === ObjectModelRelationType.OneToOne ||
      (this.origin.relation.relation_type === ObjectModelRelationType.ManyToOne && !this.origin.inverse_relation) ||
      (this.origin.relation.relation_type === ObjectModelRelationType.OneToMany && this.origin.inverse_relation)
  }

  mapValueToOption(value): IDefaultVOption {
    if (!Boolean(this.objectModelMappingOrigin)) return null
    return this.objectModelMappingOrigin.mapValueToOption(value)
  }

  get model(): ObjectModel {
    return this.origin?.model
  }

  get hasModel(): boolean {
    return Boolean(this.model)
  }

  get modelIdentifier() {
    return this.model?.client_id
  }

  get modelForFormula(): ObjectModel {
    return this.model
  }

  readonly _objectModelMappingOrigin: ModelMappingOriginObjectModel
  private get objectModelMappingOrigin(): ModelMappingOriginObjectModel {
    if (!this.hasModel) return null
    if (this._objectModelMappingOrigin) return this._objectModelMappingOrigin
    const klass = ModelMappingOriginFactory.getClass(ModelType.ObjectModel) as typeof ModelMappingOriginObjectModel
    return new klass(this.model.client_id, this.modelMapping, this.mappingOrigin)
  }

  // ******************** NESTED MODEL MAPPING ******************** //
  nestedModelMappingSourceOptions(): IOriginWrapper[] {
    return this.modelMapping.source?.relation_identifiers.map(relationIdentifier => new OriginWrapperObjectModelRelation(relationIdentifier)) ?? []
  }

  nestedModelMappingDestinationOptions(source: IOriginWrapper): IOriginWrapper[] {
    const allOptions = this.modelMapping.destination.relationIdentifiers.map(ri => {
      return new OriginWrapperObjectModelRelation(ri)
    })
    return this.modelMapping.modelMappingHandler.destinationOptionsForSource(allOptions, source)
  }

  get hasRelations(): boolean {
    return this.model?.hasRelations
  }

  async fetchRelations() {
    if (!this._hasFetchedNestedObjectModelRelations && this.hasModel && !this.model.hasRelations) {
      await this.model.fetchObjectModelRelations()
      this._hasFetchedNestedObjectModelRelations = true
    } else {
      return Promise.resolve()
    }
  }

  /**
   * Build a relation identifier for every available option for the other side
   * Do not build one if one for that relation, model, and inverse is already built
   * @param modelMappingSourceDestination {MappingOrigin}
   */
  buildRelationIdentifiers(modelMappingSourceDestination: MappingOrigin) {
    const options = MappingOriginObjectModel.availableOptions(this.model, {includeSelf: false}) as MappingOriginOptionObjectModelRelation[]
    const ris = options.reduce<RelationIdentifier[]>((agg, o) => {
      const attrs = {
        relation_client_id: o.origin.client_id,
        model_client_id: o.model.client_id,
        inverse_relation: o.inverse_relation,
      }
      let ri: RelationIdentifier = modelMappingSourceDestination.relationIdentifiers.all.find(relIdentifier => {
        return relIdentifier.isEqual(attrs)
      })
      if (!Boolean(ri)) {
        ri = new RelationIdentifier(modelMappingSourceDestination).loadFromJson(attrs)
        ri.parent = modelMappingSourceDestination
        modelMappingSourceDestination.relationIdentifiers.add(ri)
        modelMappingSourceDestination.trackCreation(ri)
      }
      agg.push(ri)
      return agg
    }, [])
    // remove relation identifiers no longer available. e.g. a model relationship was deleted
    modelMappingSourceDestination.relationIdentifiers.forEach(ri => {
      if (!ris.includes(ri)) {
        modelMappingSourceDestination.relationIdentifiers.destroy(ri)
      }
    })
  }
  // ******************** NESTED MODEL MAPPING END ******************** //

  get sourceBuiltInSetNumberForMappingValue(): BuiltInTypesSetNumber {
    return this.objectModelMappingOrigin.sourceBuiltInSetNumberForMappingValue
  }

  get sourceBuiltInSetNumberForValueOptions(): BuiltInTypesSetNumber {
    return this.objectModelMappingOrigin.sourceBuiltInSetNumberForValueOptions
  }
}